import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, EMPTY, of, retry, Subject, switchMap, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { KeycloakService } from '@atsystems/ui';
import { SKIP_ERROR_INTERCEPTOR } from '../helpers/errors.interceptor';
import { MenuConfig, UserSettings } from '../models/user-settings.model';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsApiService {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private keycloak = inject(KeycloakService);

  private retryConfig = {
    count: 3,
    delay: (retryCount: number) => timer(1000 * retryCount),
  };

  public settingsResource = rxResource({
    stream: () => this.http.get<UserSettings>(`${environment.bff_service}/user-settings/me`).pipe(
      catchError(() => of({} as UserSettings))
    ),
  });

  private localMenuConfig = signal<MenuConfig | null>(this.readCache());
  public menuConfig = computed(() => this.localMenuConfig());

  private saveSubject = new Subject<MenuConfig>();

  constructor() {
    this.saveSubject.pipe(
      debounceTime(500),
      switchMap(config => this.http.put<void>(
        `${environment.bff_service}/user-settings/me/menu`, config,
        { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) },
      ).pipe(
        retry(this.retryConfig),
        catchError(() => EMPTY),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    effect(() => {
      const server = (this.settingsResource.value() as UserSettings | undefined)?.menu ?? null;
      untracked(() => {
        const local = this.localMenuConfig();
        const winner = this.pickNewer(local, server);
        if (winner && winner !== local) {
          this.localMenuConfig.set(winner);
          this.writeCache(winner);
        }
      });
    });
  }

  public saveMenuConfig(config: MenuConfig): void {
    const next: MenuConfig = { ...config, updatedAt: Date.now() };
    this.localMenuConfig.set(next);
    this.writeCache(next);
    this.saveSubject.next(next);
  }

  public currentConfig(): MenuConfig {
    return this.menuConfig() ?? this.emptyConfig();
  }

  public resetMenuOrder(): void {
    this.saveMenuConfig({ ...this.currentConfig(), order: [] });
  }

  public resetFavorites(): void {
    this.saveMenuConfig({ ...this.currentConfig(), favorites: [] });
  }

  public resetAll(): void {
    this.saveMenuConfig(this.emptyConfig());
  }

  private emptyConfig(): MenuConfig {
    return { favorites: [], order: [], collapsed: false, updatedAt: 0 };
  }

  private pickNewer(a: MenuConfig | null, b: MenuConfig | null): MenuConfig | null {
    const ta = a?.updatedAt ?? -Infinity;
    const tb = b?.updatedAt ?? -Infinity;
    return tb > ta ? b : a;
  }

  private cacheKey(): string | null {
    const sub = this.keycloak.getUserId();
    return sub ? `ats:menu-config:${sub}` : null;
  }

  private readCache(): MenuConfig | null {
    const key = this.cacheKey();
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as MenuConfig) : null;
    } catch {
      return null;
    }
  }

  private writeCache(config: MenuConfig): void {
    const key = this.cacheKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(config));
    } catch {
    }
  }
}
